import { DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT } from "@config/apiConfig";
import { api } from "./api";
import { uploadFileToAzureBlob } from "./azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "./user/types";
import { getSasToken } from "./userApi";

export type OperationType = "LOAD" | "UNLOAD";

export type GateReason =
  | "VESSEL_CALL_UNAVAILABLE"
  | "VESSEL_CALL_NOT_APPROVED"
  | "NO_APPROVED_NOMINATION"
  | "NOT_LINKED_TO_CALL"
  | "NOT_DRY_BULK"
  | "OTHER_COMPANY"
  | "NOMINATION_LOOKUP_UNAVAILABLE";

export type NominationStatus = "APPROVED" | "CLOSED";

export interface NominationReference {
  id: string;
  refOrderNumber: number;
}

export interface OperationNominationView extends NominationReference {
  status: NominationStatus;
}

export interface OperationNominations {
  nominations: OperationNominationView[];
}

export interface AddOperationGate {
  enabled: boolean;
  reason: string | null;
  reasonCode?: GateReason | null;
  nominations?: NominationReference[];
  nominationRefOrderNumber: number | null;
}

export interface CreateCargoOperationRequest {
  id?: string;
  vesselCallId: string;
  vesselImo: string;
  operationType: OperationType;
  totalQuantity: number;
}

export interface CreatedCargoOperation {
  id: string;
  operationNumber?: number | null;
  status: string;
  operationType: OperationType;
  totalQuantity: number | null;
  vesselCallId: string;
  portCode: string | null;
  totalQuantityRemained: number | null;
}

export interface TotalQuantityUpdated {
  id: string;
  totalQuantity: number;
  totalQuantityRemained: number | null;
}

export type PartyType = "SHIPPER" | "STOCK_PILE";

export interface SequenceView {
  id: string;
  sequenceNo: number;
  hold: number;
  plannedQuantity: number | null;
  shipperCompanyCode: string | null;
  stockPileCode: string | null;
  cargoCode: string | null;
  gradeCode: string | null;
  gradeSizeCode: string | null;
  actualSize: string | null;
  status: string;
  loaderCode?: string | null;
  hopperCode?: string | null;
  transporterCompanyCode?: string | null;
  transporterCompanyName?: string | null;
  transporterContacts?: TransporterContact[] | null;
  actualQuantity?: number | null;
  remainingQuantity?: number | null;
  ats?: string | null;
  atc?: string | null;
  increments?: IncrementView[] | null;
}

export interface TransporterContact {
  person: string | null;
  number: string | null;
}

export interface IncrementView {
  id: string;
  incrementNo: number;
  state: string;
  ats: string;
  atc?: string | null;
  quantity?: number | null;
  final?: boolean | null;
}

export interface TransporterRow {
  companyCode: string | null;
  companyName: string | null;
  contacts: TransporterContact[] | null;
  planned: number | null;
  splits: PlannedSplit[] | null;
}

export interface LoaderOption {
  loaderCode: string;
  loaderName: string | null;
  loaderType: string | null;
}

export interface HopperOption {
  hopperCode: string;
  hopperName: string | null;
}

export interface TransporterOption {
  companyCode: string;
  companyName: string | null;
  contacts: TransporterContact[];
}

export interface EquipmentOptions {
  portCode: string | null;
  loaders: LoaderOption[];
  hoppers: HopperOption[];
  transporters: TransporterOption[];
}

export interface EquipmentRequest {
  loaderCode: string;
  hopperCode: string;
  transporterCompanyCode: string;
}

export interface EquipmentResult {
  sequenceId: string;
  status: string | null;
}

export interface IncrementStartRequest {
  ats?: string | null;
}

export interface IncrementCompleteRequest {
  quantity: number;
  atc: string;
  ats?: string | null;
  final: boolean;
}

export interface IncrementOutcome {
  incrementId: string;
  sequenceId: string;
  sequenceStatus: string;
  firstIncrement?: boolean | null;
  incrementNo?: number | null;
  sequenceCompleted?: boolean | null;
}

export type FinalDraftSurveyMode = "TOTAL" | "PER_SHIPPER";

export interface IntermediaryAmountEntry {
  shipperCompanyCode: string;
  amount: number;
}

export type FinalDraftSurveyRequest =
  | { mode: "TOTAL"; total: number }
  | { mode: "PER_SHIPPER"; intermediaryAmounts: IntermediaryAmountEntry[] };

export interface FinalDraftSurveyResult {
  status: string;
  finalDraftSurvey?: number | null;
  finalDraftSurveyMode?: FinalDraftSurveyMode | null;
  intermediaryAmounts?: IntermediaryAmountEntry[] | null;
}

export interface ToleranceEntry {
  shipperCompanyCode: string;
  tolerancePct: number;
}

export interface ToleranceUpdateRequest {
  tolerances: ToleranceEntry[];
}

export interface ToleranceUpdateResult {
  tolerances: ToleranceEntry[];
}

export interface ToleranceChange {
  shipperCompanyCode: string;
  oldTolerancePct: number;
  newTolerancePct: number;
  changedBy?: string | null;
  changedAt: string;
}

export interface ToleranceChangeList {
  changes: ToleranceChange[];
}

export interface PlannedSplit {
  cargoCode: string | null;
  gradeSizeCode: string | null;
  planned: number | null;
}

export interface ShipperRow {
  shipperCompanyCode: string | null;
  planned: number | null;
  readiness: "PENDING_READINESS" | "READY" | null;
  actual?: number | null;
  intermediaryAmount?: number | null;
  tolerancePct?: number | null;
  adjusted?: number | null;
  verdict?: "CLOSE" | "DISCREPANCY" | null;
  splits: PlannedSplit[] | null;
}

export interface StockPileRow {
  stockPileCode: string | null;
  planned: number | null;
  actual?: number | null;
  splits: PlannedSplit[] | null;
}

export interface CargoOperationDetail {
  id: string;
  operationNumber?: number | null;
  status: string | null;
  operationType: OperationType;
  totalQuantity: number | null;
  totalQuantityRemained: number | null;
  vesselCallId: string | null;
  sequences: SequenceView[] | null;
  shippers: ShipperRow[] | null;
  stockPiles: StockPileRow[] | null;
  latestRejection?: LatestRejection | null;
  reviewTask?: ReviewTask | null;
  transporters?: TransporterRow[] | null;
  finalBeltScale?: number | null;
  firstAts?: string | null;
  lastAtc?: string | null;
  atb?: string | null;
  atbBerth?: string | null;
  finalDraftSurvey?: number | null;
  finalDraftSurveyMode?: FinalDraftSurveyMode | null;
  /** Operations only (the BFF's field policy): absent for a Shipping Agent and a Shipper. */
  requestedBy?: string | null;
  requestingAgencyCode?: string | null;
  requestingAgencyName?: string | null;
  /** DBCO-0030 - absent for a Shipper (the BFF's field policy), so the Documents tab is not rendered (R-34). */
  attachments?: Attachment[] | null;
}

export interface ReviewTask {
  taskId: string;
  status: string;
  owner?: string | null;
  claimedByCaller: boolean;
  modifiedOn?: string | null;
}

export interface ReviewDecisionResult {
  status: string;
  decision: string;
}

export interface LatestRejection {
  reason: string;
  rejectedAt: string;
  rejectedBy: string;
}

/** DBCO-0030 - Bay Hire's attachment shape, mirrored field for field by bff-drybulk-cargo-ops. */
export interface AttachmentUploadRequest {
  attachmentType: string;
  documentUrl: string;
  originalFileName?: string;
  contentType?: string;
  fileSizeBytes?: number;
  documentStatus?: string;
}

/** A file already in Blob storage (temp/), waiting to be attached. */
export type StagedAttachment = AttachmentUploadRequest;

export interface Attachment {
  id: string;
  attachmentType: string;
  documentUrl: string;
  originalFileName?: string | null;
  contentType?: string | null;
  fileSizeBytes?: number | null;
  uploadedBy?: string | null;
  uploadedByRole?: string | null;
  uploadedAt?: string | null;
  documentStatus?: string | null;
  messageId?: string | null;
  threadKey?: string | null;
}

export interface MessageFile {
  documentUrl: string;
  originalFileName?: string;
  contentType?: string;
  fileSizeBytes?: number;
  documentStatus?: string;
}

export interface MessageAttachmentsRequest {
  messageId: string;
  threadKey: string;
  files: MessageFile[];
}

export interface MessageAttachment extends Attachment {
  messageId: string;
  threadKey: string;
}

export interface SequenceRequest {
  plannedQuantity: number;
  hold: number;
  shipperCompanyCode: string;
  stockPileCode: string;
  cargoCode: string;
  gradeSizeCode: string;
  actualSize: string | null;
}

export interface OperationSequencesResponse {
  operationId: string;
  totalQuantityRemained: number | null;
  sequences: SequenceView[] | null;
}

export interface PartyResponse {
  code: string | null;
  type: PartyType | null;
  longName: string | null;
  companyCode: string | null;
  contactPerson: string | null;
  email: string | null;
  mobilePhone: string | null;
  officePhone: string | null;
}

export interface CargoCodeOption {
  cargoCode: string;
  description: string | null;
  gradeCode: string | null;
}

export interface GradeOption {
  gradeCode: string;
  description: string | null;
}

export interface StockPileOption {
  stockPileCode: string;
  name: string | null;
}

export interface ShipperOption {
  companyCode: string;
  name: string | null;
}

export interface SubmitState {
  status: string;
  totalQuantityRemained: number | null;
}

export type CargoOperationSortBy =
  | "CREATED_AT"
  | "STATUS"
  | "OPERATION_TYPE"
  | "TOTAL_QUANTITY"
  | "VESSEL_IMO"
  | "REQUESTING_AGENCY"
  | "OPERATION_NUMBER";

export interface CargoOperationSearchRequest {
  vesselCallId?: string | null;
  statuses?: string[] | null;
  sortBy?: CargoOperationSortBy;
  sortDirection?: "ASC" | "DESC";
  pageNumber?: number;
  pageSize?: number;
  operationTypes?: OperationType[] | null;
  vesselImo?: string | null;
  operationNumber?: number | null;
  nominationRefOrderNumber?: number | null;
  agencyCodes?: string[] | null;
  createdFrom?: string | null;
  createdTo?: string | null;
  vcn?: string | null;
  lastPorts?: string[] | null;
  nextPorts?: string[] | null;
  arrivalLocations?: string[] | null;
  vesselCallStatuses?: string[] | null;
  vesselTypes?: string[] | null;
  flags?: string[] | null;
  vesselName?: string | null;
  gradeSizes?: string[] | null;
  totalQuantityFrom?: number | null;
  totalQuantityTo?: number | null;
  atsFrom?: string | null;
  atsTo?: string | null;
  atcFrom?: string | null;
  atcTo?: string | null;
}

export interface CargoOperationGridRow {
  id: string;
  operationType: OperationType | null;
  totalQuantity: number | null;
  gradeSizes: string[] | null;
  ats: string | null;
  atc: string | null;
  status: string | null;
  createdAt: string | null;
  requestedBy?: string | null;
  requestingAgencyCode?: string | null;
  requestingAgencyName?: string | null;
  vesselImo?: string | null;
  vesselCallId?: string | null;
  vcn?: string | null;
  vesselCallStatus?: string | null;
  lastPort?: string | null;
  nextPort?: string | null;
  arrivalLocation?: string | null;
  vesselType?: string | null;
  flag?: string | null;
  vesselName?: string | null;
  operationNumber?: number | null;
  atb?: string | null;
}

export interface CargoOperationSearchPage {
  totalRecordCount: number;
  records: CargoOperationGridRow[] | null;
}

export interface ReadinessResult {
  readiness: "READY";
}

/** DBCO-0059 - the delay shapes, copied field for field from the BFF spec (`bff-drybulk-cargo-ops-api.yaml`, DBCO-0052). */
export interface DelayView {
  id: string;
  holdNo: number;
  delayTypeCode: string;
  delayTypeName: string | null;
  delayCode: string;
  delayDescription: string | null;
  startAt: string;
  endAt: string;
  durationMinutes: number;
  loaderCode: string | null;
  remarks: string | null;
  createdBy: string;
  createdAt: string;
  modifiedBy?: string | null;
  modifiedAt?: string | null;
}

export type OperationLogSource = "STATUS_TRANSITION" | "TOLERANCE_CHANGE";

export type OperationLogAction =
  | "OPERATION_CREATED"
  | "OPERATION_SUBMITTED"
  | "OPERATION_REJECTED"
  | "OPERATION_RESUBMITTED"
  | "OPERATION_APPROVED"
  | "SHIPPER_READINESS_REQUESTED"
  | "SHIPPER_READY"
  | "OPERATION_SHIPPERS_READY"
  | "SEQUENCE_UPCOMING"
  | "SEQUENCE_STARTED"
  | "OPERATION_STARTED"
  | "SEQUENCE_COMPLETED"
  | "OPERATION_AWAITING_FINAL_DRAFT_SURVEY"
  | "OPERATION_COMPLETED"
  | "OPERATION_DELETED"
  | "TOLERANCE_CHANGED"
  | "STATUS_CHANGED";

export type OperationLogEntityType = "OPERATION" | "SEQUENCE" | "SHIPPER";

export type OperationLogActorParty = "OPERATIONS" | "SHIPPING_AGENT" | "SHIPPER";

export interface OperationLogEntry {
  id: string;
  source: OperationLogSource;
  action: OperationLogAction;
  label: string;
  entityType?: OperationLogEntityType | null;
  sequenceNo?: number | null;
  shipperCompanyCode?: string | null;
  fromStatus?: string | null;
  toStatus?: string | null;
  oldTolerancePct?: number | null;
  newTolerancePct?: number | null;
  actorParty?: OperationLogActorParty | null;
  createdBy?: string | null;
  changedBy?: string | null;
  createdAt: string;
}

export interface OperationLog {
  filtered: boolean;
  filterLabel?: string | null;
  totalRecordCount: number;
  records: OperationLogEntry[];
}

export interface DelayList {
  delays: DelayView[];
  totalDurationMinutes: number;
}

export interface DelayRequest {
  holdNo: number;
  delayTypeCode: string;
  delayCode: string;
  startAt: string;
  endAt: string;
  loaderCode?: string | null;
  remarks?: string | null;
}

export interface DelayTypeOption {
  delayTypeCode: string;
  delayTypeName?: string | null;
}

export interface DelayCodeOption {
  delayCode: string;
  delayDescription?: string | null;
  delayTypeCode?: string | null;
}

/** DBCO-0059 - the Final Loading Report shapes (BFF spec, DBCO-0053 / DBCO-0057). */
export type ReportContactRole = "SHIPPER" | "STOCK_PILE" | "TRANSPORTER" | "AGENCY";

export type ReportContactState =
  | "CURRENT"
  | "CAPTURED"
  | "NO_LICENCE"
  | "UNAVAILABLE"
  | "PENDING"
  | "ASSIGNED";

export type MissingGeneralInfoField = "WATER_CONSUMPTION" | "MAX_FEED_RATE";

export interface ReportGeneralInfo {
  vesselName: string | null;
  shipBerthed: string | null;
  loadingCommenced: string | null;
  loadingCompleted: string | null;
  shipDeparture: string | null;
  shipDepartureUnavailable: boolean;
  aggregateSizes: string[];
  reportDate: string;
  viaName: string | null;
  viaNumber: string | null;
  waterConsumption?: number | null;
  maxFeedRate?: number | null;
  missingGeneralInfo: MissingGeneralInfoField[];
  finalBeltScale: number;
  finalDraftSurvey?: number | null;
}

export interface ReportCombinationValue {
  cargoCode: string;
  gradeSizeCode: string;
  value: number | null;
}

export interface ReportCombination {
  number: number;
  shipperCompanyCode: string;
  stockPileCode: string;
  adjusted: ReportCombinationValue[];
  totalAdjusted: number | null;
}

export interface ReportColumn {
  cargoCode: string;
  gradeSizeCode: string;
}

export interface ReportSequenceRow {
  sequenceNo: number;
  hold: number;
  stockPileCode: string;
  shipperCompanyCode: string;
  cargoCode: string;
  gradeSizeCode: string;
  hopperCode: string | null;
  actual: number;
}

export interface ReportColumnTotal {
  cargoCode: string;
  gradeSizeCode: string;
  actual: number;
}

export interface ReportHopperTotal {
  hopperCode: string | null;
  actual: number;
}

export interface ReportShipperSplit {
  shipperCompanyCode: string;
  cargoCode: string;
  gradeSizeCode: string;
  planned: number;
  actual: number;
  adjusted: number | null;
}

export interface ReportHoldColumn {
  cargoCode: string;
  gradeSizeCode: string;
  planned: number;
  actual: number;
}

export interface ReportHold {
  hold: number;
  planned: number;
  actual: number;
  byColumn: ReportHoldColumn[];
}

export interface ReportContactEntry {
  role: ReportContactRole;
  companyCode: string;
  state: ReportContactState | null;
  applicantName: string | null;
  applicantPosition: string | null;
  applicantEmail: string | null;
  companyEmail: string | null;
  telephoneNumber: string | null;
  mobileNumber: string | null;
  capturedAt: string | null;
}

export interface FinalLoadingReport {
  generalInfo: ReportGeneralInfo;
  combinations: ReportCombination[];
  columns: ReportColumn[];
  sequences: ReportSequenceRow[];
  columnTotals: ReportColumnTotal[];
  sequencesTotal: number;
  hopperTotals: ReportHopperTotal[];
  shipperSplit: ReportShipperSplit[];
  holds: ReportHold[];
  contacts: ReportContactEntry[];
  contactsUnavailable: boolean;
}

export interface GeneralInfoUpdateRequest {
  waterConsumption: number;
  maxFeedRate: number;
}

/** DBCO-0059 - the Final Delay Report shapes (BFF spec, DBCO-0054). */
export interface FinalDelayReportHeader {
  operationNumber: number;
  vesselName: string | null;
  shipBerthed: string | null;
  loadingCommenced: string | null;
  loadingCompleted: string | null;
  shipDeparture: string | null;
  shipDepartureUnavailable: boolean;
  reportDate: string;
  destination?: string | null;
  destinationUnavailable: boolean;
  usedHoppers: string[];
  submittedBy: string | null;
}

export interface DelayReportType {
  code: string;
  name: string;
}

export interface DelayReportRow {
  no: number;
  delayId: string;
  startAt: string;
  endAt: string;
  holdNo: number;
  delayTypeCode: string;
  delayCode: string;
  codeDescription: string;
  loaderCode: string | null;
  remarks: string | null;
  durationMinutes: number;
  minutesByType: (number | null)[];
}

export interface DelayTypeSummary {
  code: string;
  name: string;
  minutes: number;
  hours: number;
}

export interface DelayReportPerformance {
  grossLoadingMinutes: number | null;
  grossLoadingHours: number | null;
  netLoadingMinutes: number | null;
  netLoadingHours: number | null;
  grossLoadingRate: number | null;
  netLoadingRate: number | null;
  inBerthHours: number | null;
  draftSurvey: number | null;
  beltScale: number | null;
  difference: number | null;
  differencePct: number | null;
  totalDelayHours: number;
}

export interface DelayReportPeriod {
  month: string;
  year: number;
  averageGrossLoadingRate: number | null;
  averageNetLoadingRate: number | null;
  monthlyThroughput: number;
  yearlyThroughput: number;
}

export interface FinalDelayReport {
  header: FinalDelayReportHeader;
  delayTypes: DelayReportType[];
  rows: DelayReportRow[];
  totalsByType: number[];
  totalMinutes: number;
  summaryByType: DelayTypeSummary[];
  performance: DelayReportPerformance;
  period: DelayReportPeriod;
}

interface TelemetryOptions {
  transactionId?: string;
}

const BASE = "drybulk-cargo-operations";

export const drybulkCargoOperationsService = {
  getAddOperationGate(vesselImo: string, vesselCallId: string) {
    return api.get<AddOperationGate>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/add-operation-gate`,
      {
        params: { vesselImo, vesselCallId },
      },
    );
  },

  create(request: CreateCargoOperationRequest, { transactionId }: TelemetryOptions = {}) {
    return api.post<CreatedCargoOperation>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      BASE,
      request,
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  updateTotalQuantity(id: string, totalQuantity: number, { transactionId }: TelemetryOptions = {}) {
    return api.patch<TotalQuantityUpdated>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/total-quantity`,
      {
        totalQuantity,
      },
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  submitPrecheck(id: string) {
    return api.get<SubmitState>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/submit-precheck`,
    );
  },

  submit(id: string, { transactionId }: TelemetryOptions = {}) {
    return api.post<SubmitState>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/submit`,
      undefined,
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  deleteOperation(id: string, { transactionId }: TelemetryOptions = {}) {
    return api.delete<void>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}`,
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  /**
   * DBCO-0030 - mints a WRITE SAS for DRYBULK_CARGO_OPS and PUTs the file straight to Blob; no bytes transit a
   * cargo-ops service (ADR-005). The returned reference is attached later by `uploadAttachment`.
   */
  async stageAttachment(file: File, attachmentType: string): Promise<StagedAttachment> {
    const { blobUrl, sasToken } = await getSasToken({
      prefix: SasTokenPrefix.DRYBULK_CARGO_OPS,
      contentType: file.type || null,
      privilege: SasTokenPrivilege.WRITE,
      blobUrl: null,
    });
    await uploadFileToAzureBlob({ file, blobUrl, sasToken, certificateType: attachmentType });
    return {
      attachmentType,
      documentUrl: blobUrl,
      originalFileName: file.name,
      contentType: file.type || undefined,
      fileSizeBytes: file.size,
    };
  },

  uploadAttachment(
    id: string,
    request: AttachmentUploadRequest,
    { transactionId }: TelemetryOptions = {},
  ) {
    return api.post<Attachment>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/attachments`,
      request,
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  attachMessageFiles(
    id: string,
    request: MessageAttachmentsRequest,
    { transactionId }: TelemetryOptions = {},
  ) {
    return api.post<MessageAttachment[]>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/message-attachments`,
      request,
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  listMessageAttachments(id: string, threadKey?: string) {
    return api.get<MessageAttachment[]>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/message-attachments`,
      { params: threadKey ? { threadKey } : {} },
    );
  },

  search(request: CargoOperationSearchRequest) {
    return api.post<CargoOperationSearchPage>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/search-paged`,
      request,
    );
  },

  claim(id: string) {
    return api.post<ReviewTask>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/claim`,
      undefined,
    );
  },

  unclaim(id: string) {
    return api.delete<ReviewTask>(DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT, `${BASE}/${id}/claim`);
  },

  approve(id: string, remark: string, { transactionId }: TelemetryOptions = {}) {
    return api.post<ReviewDecisionResult>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/approve`,
      remark.trim() === "" ? {} : { remark },
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  reject(id: string, reason: string, { transactionId }: TelemetryOptions = {}) {
    return api.post<ReviewDecisionResult>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/reject`,
      { reason },
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  confirmReadiness(id: string, shipperCode: string, { transactionId }: TelemetryOptions = {}) {
    return api.post<ReadinessResult>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/shippers/${encodeURIComponent(shipperCode)}/readiness`,
      undefined,
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  getDetail(id: string) {
    return api.get<CargoOperationDetail>(DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT, `${BASE}/${id}`);
  },

  addSequence(id: string, request: SequenceRequest, { transactionId }: TelemetryOptions = {}) {
    return api.post<OperationSequencesResponse>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/sequences`,
      request,
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  updateSequence(
    id: string,
    sequenceId: string,
    request: SequenceRequest,
    { transactionId }: TelemetryOptions = {},
  ) {
    return api.put<OperationSequencesResponse>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/sequences/${sequenceId}`,
      request,
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  removeSequence(id: string, sequenceId: string, { transactionId }: TelemetryOptions = {}) {
    return api.delete<OperationSequencesResponse>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/sequences/${sequenceId}`,
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  getParty(id: string, code: string, type: PartyType) {
    return api.get<PartyResponse>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/parties/${encodeURIComponent(code)}`,
      { params: { type } },
    );
  },

  getEquipmentOptions(id: string) {
    return api.get<EquipmentOptions>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/equipment-options`,
    );
  },

  assignEquipment(
    id: string,
    sequenceId: string,
    request: EquipmentRequest,
    { transactionId }: TelemetryOptions = {},
  ) {
    return api.put<EquipmentResult>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/sequences/${sequenceId}/equipment`,
      request,
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  startIncrement(
    id: string,
    sequenceId: string,
    request: IncrementStartRequest,
    { transactionId }: TelemetryOptions = {},
  ) {
    return api.post<IncrementOutcome>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/sequences/${sequenceId}/increments`,
      request,
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  completeIncrement(
    id: string,
    sequenceId: string,
    incrementId: string,
    request: IncrementCompleteRequest,
    { transactionId }: TelemetryOptions = {},
  ) {
    return api.patch<IncrementOutcome>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/sequences/${sequenceId}/increments/${incrementId}`,
      request,
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  enterFinalDraftSurvey(
    id: string,
    request: FinalDraftSurveyRequest,
    { transactionId }: TelemetryOptions = {},
  ) {
    return api.post<FinalDraftSurveyResult>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/final-draft-survey`,
      request,
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  editTolerance(
    id: string,
    request: ToleranceUpdateRequest,
    { transactionId }: TelemetryOptions = {},
  ) {
    return api.put<ToleranceUpdateResult>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/tolerance`,
      request,
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  getOperationNominations(id: string) {
    return api.get<OperationNominations>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/nominations`,
    );
  },

  toleranceChanges(id: string) {
    return api.get<ToleranceChangeList>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/tolerance-changes`,
    );
  },

  listDelays(id: string) {
    return api.get<DelayList>(DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT, `${BASE}/${id}/delays`);
  },

  getOperationLog(id: string, pageNumber: number, pageSize: number) {
    return api.get<OperationLog>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/operation-log`,
      {
        params: { pageNumber, pageSize },
      },
    );
  },

  addDelay(id: string, request: DelayRequest, { transactionId }: TelemetryOptions = {}) {
    return api.post<DelayView>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/delays`,
      request,
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  updateDelay(
    id: string,
    delayId: string,
    request: DelayRequest,
    { transactionId }: TelemetryOptions = {},
  ) {
    return api.put<DelayView>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/delays/${delayId}`,
      request,
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  deleteDelay(id: string, delayId: string, { transactionId }: TelemetryOptions = {}) {
    return api.delete<void>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/delays/${delayId}`,
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  delayTypes() {
    return api.get<DelayTypeOption[]>(DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT, "delay-types");
  },

  delayCodes(delayTypeCode: string) {
    return api.get<DelayCodeOption[]>(DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT, "delay-codes", {
      params: { delayTypeCode },
    });
  },

  finalLoadingReport(id: string) {
    return api.get<FinalLoadingReport>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/final-loading-report`,
    );
  },

  updateGeneralInfo(
    id: string,
    request: GeneralInfoUpdateRequest,
    { transactionId }: TelemetryOptions = {},
  ) {
    return api.put<ReportGeneralInfo>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/final-loading-report/general-info`,
      request,
      ...(transactionId ? [{ transactionId }] : []),
    );
  },

  finalDelayReport(id: string) {
    return api.get<FinalDelayReport>(
      DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT,
      `${BASE}/${id}/final-delay-report`,
    );
  },

  getCargoCodes() {
    return api.get<CargoCodeOption[]>(DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT, "cargo-codes");
  },

  getGrades() {
    return api.get<GradeOption[]>(DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT, "grades");
  },

  getStockPiles() {
    return api.get<StockPileOption[]>(DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT, "stock-piles");
  },

  getShippers() {
    return api.get<ShipperOption[]>(DRYBULK_CARGO_OPS_BFF_SERVICE_SEGMENT, "shippers");
  },
};

/** DBCO-0059 - the report PDFs for `printRequestedTemplate` (Bay Hire's `getBayHirePrintDocument` shape). */
export const printFinalLoadingReport = ({
  service,
  id,
}: {
  service: string;
  id: string;
}): Promise<Blob> =>
  api.get<Blob>(service, `${BASE}/${id}/final-loading-report:print`, { responseType: "blob" });

export const printFinalDelayReport = ({
  service,
  id,
}: {
  service: string;
  id: string;
}): Promise<Blob> =>
  api.get<Blob>(service, `${BASE}/${id}/final-delay-report:print`, { responseType: "blob" });
